import React, { useState, useEffect, FC } from 'react';
import { scrollToItem, CardTypes } from './utils';
import { trackClick } from './tracking';
import Card from './card';

interface LiveBlogProps {
  app: boolean;
  appOs: string;
  appRef: string;
  contentId: string;
  expired: string;
  marketTimezone: string;
}

const {
  nbc: {
    brand,
    cardId,
  },
} = window;

const LiveBlog: FC<LiveBlogProps> = ({
  app = false,
  appOs = '',
  appRef = '',
  contentId,
  expired,
  marketTimezone,
}) => {
  const baseUrl = `/apps/liveblog/v1/post/${contentId}`;
  const [cards, setCards] = useState<CardTypes[]>([]);
  const [newCards, setNewCards] = useState<CardTypes[]>([]);
  const [updateCount, setUpdateCount] = useState(0);
  const [hasUpdates, setHasUpdates] = useState(false);

  const updateTitle = () => {
    const { title: documentTitle } = document;
    const oldTitle = documentTitle.split(') ').pop();

    document.title = `${hasUpdates ? `(${updateCount}) ` : ''}${oldTitle}`;
  };

  const fetchCards = async (isUpdate) => {
    const response = await fetch(window.wp.url.addQueryArgs(
      baseUrl,
      {
        appDeeplink: app ? 1 : 0,
        os: appOs || '',
        ref: appRef ? 'app' : '',
        version: 1,
      }
    ));

    const {
      items: fetchedCards = [],
    } = await response.json();

    if ((fetchedCards.length > cards.length) && isUpdate) {
      setNewCards(fetchedCards);
      setUpdateCount(fetchedCards.length - cards.length);
      setHasUpdates(true);
    } else {
      setCards(fetchedCards);
    }
  };

  const loadUpdates = () => {
    fetchCards(false);
    setNewCards([]);
    setHasUpdates(false);
    setUpdateCount(0);
    updateTitle();
    scrollToItem('.liveblog', false);
    trackClick('new updates - click', 'liveblog');
  };

  const loadCards = async () => {
    await fetchCards(false);
    if (null !== cardId) {
      scrollToItem(`[data-card-id="${cardId}"]`, true);
    }
  };

  const trackingEventListener = (elements: NodeListOf<Element>, eventName: string, linkName: string, moduleName: string) => {
    if (elements) {
      Array.from(elements).forEach((element) => {
        const parentCardId = element.closest('.liveblog-card')?.getAttribute('data-card-id') || '';
        const parentCardTitle = element.closest('.liveblog-card')?.getAttribute('data-card-title') || '';
        element.addEventListener(eventName, () => trackClick(linkName, moduleName, parentCardId, parentCardTitle));
        element.setAttribute('data-tracking', 'true');
      });
    }
  };

  // #region Initial Load
  useEffect(() => {
    loadCards();
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // #region Tracking event listeners for read more and gallery navigation
    const readMoreElements = document.querySelectorAll('.liveblog-card__read-more-link:not([data-tracking=true])');
    const galleryNextElements = document.querySelectorAll('.slick-next:not([data-tracking=true])');
    const galleryPrevElements = document.querySelectorAll('.slick-prev:not([data-tracking=true])');

    trackingEventListener(readMoreElements, 'click', 'read full article', 'liveblog');
    trackingEventListener(galleryNextElements, 'click', 'click right', 'gallery card');
    trackingEventListener(galleryPrevElements, 'click', 'click left', 'gallery card');

    // #region Check for Update Interval
    if (! expired) {
      const interval = setInterval(() => fetchCards(true), 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [cards, newCards]);

  useEffect(() => {
    updateTitle();
  }, [hasUpdates, updateCount]);

  const updateCountValue = 0 !== updateCount ? `+${updateCount} ` : '';

  let updateCountText = 1 === updateCount ? '+1 New Update' : `${updateCountValue}New Updates`;

  if ('telemundo' === brand) {
    updateCountText = 1 === updateCount ? '+1 Actualización' : `${updateCountValue}Actualizaciónes`;
  }

  if (0 < cards.length) {
    return (
      <div className={`liveBlog liveBlog--${expired ? 'expired' : 'live'}`}>
        <button
          className={`liveBlog__update-button ${hasUpdates ? 'liveBlog__update-button-visible' : ''}`}
          type="button"
          aria-label="new update button"
          value={updateCountText}
          disabled={! hasUpdates}
          onClick={() => loadUpdates()}
        >
          {updateCountText}
        </button>
        {
          cards.map((card) => (
            <Card
              key={card.network_object_id}
              card={card}
              expired={expired}
              marketTimezone={marketTimezone}
            />
          ))
        }
      </div>
    );
  }
  return null;
};

export default LiveBlog;
